import React, { useState, useEffect, useRef } from 'react';
import { Clock, Gem, User, Maximize2, X } from 'lucide-react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

const initialLots = [
  {
    id: 1,
    name: "Colombian Emerald - 5.2ct",
    image: "https://images.unsplash.com/photo-1600119612651-0db31b3a7baa?w=400&q=80",
    description: "Vivid green Colombian emerald, excellent clarity",
    currentBid: 12500,
    minIncrement: 500,
    endTime: Date.now() + 3600000,
    color: 0x50C878,
    bids: []
  },
  {
    id: 2,
    name: "Zambian Emerald - 3.8ct",
    image: "https://images.pexels.com/photos/32786083/pexels-photo-32786083.jpeg?w=400&q=80",
    description: "Deep green Zambian emerald with excellent saturation",
    currentBid: 8200,
    minIncrement: 300,
    endTime: Date.now() + 5400000,
    color: 0x046307,
    bids: []
  },
  {
    id: 3,
    name: "Brazilian Emerald - 7.1ct",
    image: "https://images.pexels.com/photos/13712542/pexels-photo-13712542.jpeg?w=400&q=80",
    description: "Large Brazilian emerald, minor inclusions",
    currentBid: 15800,
    minIncrement: 600,
    endTime: Date.now() + 7200000,
    color: 0x5DBE8A,
    bids: []
  },
  {
    id: 4,
    name: "Ethiopian Emerald - 4.5ct",
    image: "https://images.pexels.com/photos/13595681/pexels-photo-13595681.jpeg?w=400&q=80",
    description: "Bright green Ethiopian emerald, eye-clean",
    currentBid: 9500,
    minIncrement: 400,
    endTime: Date.now() + 1800000,
    color: 0x3CB371,
    bids: []
  }
];

const Emerald3DViewer = ({ color, isModal = false }) => {
  const mountRef = useRef(null);
  const gemRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    mountRef.current.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight1.position.set(5, 5, 5);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(-5, -5, -5);
    scene.add(directionalLight2);

    const pointLight = new THREE.PointLight(0xffffff, 1, 100);
    pointLight.position.set(0, 3, 0);
    scene.add(pointLight);

    // Load GLB emerald model
    const loader = new GLTFLoader();
    
    loader.load(
      '/models/emerald.glb',
      (gltf) => {
        const model = gltf.scene;
        
        // Auto-scale to fit viewer
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.5 / maxDim;
        model.scale.set(scale, scale, scale);
        
        // Center the model
        box.setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        
        // Apply color tint
        model.traverse((child) => {
          if (child.isMesh && child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                mat.color = new THREE.Color(color);
                mat.needsUpdate = true;
              });
            } else {
              child.material.color = new THREE.Color(color);
              child.material.needsUpdate = true;
            }
          }
        });
        
        scene.add(model);
        gemRef.current = model;
        console.log('✅ GLB emerald model loaded successfully!');
      },
      (progress) => {
        const percent = (progress.loaded / progress.total * 100).toFixed(0);
        console.log(`Loading emerald: ${percent}%`);
      },
      (error) => {
        console.error('Error loading GLB:', error);
        createProceduralEmerald();
      }
    );

    const createProceduralEmerald = () => {
      const gemGroup = new THREE.Group();
      
      const bodyGeometry = new THREE.OctahedronGeometry(1, 0);
      const gemMaterial = new THREE.MeshPhysicalMaterial({
        color: color,
        metalness: 0.1,
        roughness: 0.1,
        transparent: true,
        opacity: 0.9,
        clearcoat: 1,
        clearcoatRoughness: 0.1,
        reflectivity: 1,
      });
      
      const body = new THREE.Mesh(bodyGeometry, gemMaterial);
      body.scale.set(1, 1.2, 0.8);
      gemGroup.add(body);

      const facetGeometry = new THREE.BoxGeometry(0.3, 0.2, 0.3);
      const facetMaterial = new THREE.MeshPhysicalMaterial({
        color: color,
        metalness: 0,
        roughness: 0.05,
        transparent: true,
        opacity: 0.95,
        clearcoat: 1,
      });

      for (let i = 0; i < 8; i++) {
        const facet = new THREE.Mesh(facetGeometry, facetMaterial);
        const angle = (i / 8) * Math.PI * 2;
        facet.position.set(Math.cos(angle) * 0.6, 0.8, Math.sin(angle) * 0.6);
        facet.rotation.y = angle;
        facet.scale.set(1, 0.5, 1);
        gemGroup.add(facet);
      }

      gemGroup.rotation.x = 0.2;
      scene.add(gemGroup);
      gemRef.current = gemGroup;
      console.log('Using procedural emerald fallback');
    };

    // Mouse and touch interaction
    let isDragging = false;
    let previousPosition = { x: 0, y: 0 };

    const onPointerDown = (e) => {
      isDragging = true;
      const clientX = e.touches ? e.touches[0].clientX : e.offsetX;
      const clientY = e.touches ? e.touches[0].clientY : e.offsetY;
      previousPosition = { x: clientX, y: clientY };
    };

    const onPointerMove = (e) => {
      if (isDragging && gemRef.current) {
        const clientX = e.touches ? e.touches[0].clientX : e.offsetX;
        const clientY = e.touches ? e.touches[0].clientY : e.offsetY;
        
        const deltaX = clientX - previousPosition.x;
        const deltaY = clientY - previousPosition.y;
        
        gemRef.current.rotation.y += deltaX * 0.01;
        gemRef.current.rotation.x += deltaY * 0.01;
        
        previousPosition = { x: clientX, y: clientY };
      }
    };

    const onPointerUp = () => {
      isDragging = false;
    };

    renderer.domElement.addEventListener('mousedown', onPointerDown);
    renderer.domElement.addEventListener('mousemove', onPointerMove);
    renderer.domElement.addEventListener('mouseup', onPointerUp);
    renderer.domElement.addEventListener('touchstart', onPointerDown);
    renderer.domElement.addEventListener('touchmove', onPointerMove);
    renderer.domElement.addEventListener('touchend', onPointerUp);

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      if (!isDragging && gemRef.current) {
        gemRef.current.rotation.y += 0.005;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      renderer.domElement.removeEventListener('mousedown', onPointerDown);
      renderer.domElement.removeEventListener('mousemove', onPointerMove);
      renderer.domElement.removeEventListener('mouseup', onPointerUp);
      renderer.domElement.removeEventListener('touchstart', onPointerDown);
      renderer.domElement.removeEventListener('touchmove', onPointerMove);
      renderer.domElement.removeEventListener('touchend', onPointerUp);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [color]);

  return (
    <div 
      ref={mountRef} 
      className={`${isModal ? 'w-full h-full' : 'w-full h-64'} cursor-grab active:cursor-grabbing`}
      style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
    />
  );
};

const EmeraldAuction = () => {
  const [lots, setLots] = useState(initialLots);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [selectedLot, setSelectedLot] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [view3D, setView3D] = useState({});
  const [modalLot, setModalLot] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const handleLogin = () => {
    if (loginForm.username) {
      setUser({ username: loginForm.username });
      setLoginForm({ username: '', password: '' });
    }
  };

  const handleBid = (lotId) => {
    const lot = lots.find(l => l.id === lotId);
    const bid = parseFloat(bidAmount);
    
    if (bid >= lot.currentBid + lot.minIncrement) {
      const newBid = {
        amount: bid,
        bidder: user.username,
        time: Date.now()
      };
      
      setLots(lots.map(l => {
        if (l.id === lotId) {
          return {
            ...l,
            currentBid: bid,
            bids: [...l.bids, newBid]
          };
        }
        return l;
      }));
      
      setBidAmount('');
      alert('Bid placed successfully!');
    } else {
      alert(`Minimum bid must be $${(lot.currentBid + lot.minIncrement).toLocaleString()}`);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const toggle3DView = (lotId) => {
    setView3D(prev => ({ ...prev, [lotId]: !prev[lotId] }));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <Gem className="w-12 h-12 text-emerald-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">GreenGlow Auctions</h1>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleLogin}
              className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition"
            >
              Login to Bid
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100">
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Gem className="w-8 h-8 text-emerald-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-800">GreenGlow Auctions</h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-gray-700">
              <User className="w-5 h-5 mr-2" />
              <span className="font-medium">{user.username}</span>
            </div>
            <button
              onClick={() => setUser(null)}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {modalLot && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-3/4 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-2xl font-bold text-gray-800">{modalLot.name}</h2>
              <button
                onClick={() => setModalLot(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1">
              <Emerald3DViewer color={modalLot.color} isModal={true} />
            </div>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {lots.map(lot => {
            const timeRemaining = lot.endTime - currentTime;
            const isEnded = timeRemaining <= 0;
            const show3D = view3D[lot.id];
            
            return (
              <div key={lot.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition">
                <div className="relative">
                  {show3D ? (
                    <Emerald3DViewer color={lot.color} />
                  ) : (
                    <img 
                      src={lot.image} 
                      alt={lot.name}
                      className="w-full h-64 object-cover"
                    />
                  )}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button
                      onClick={() => toggle3DView(lot.id)}
                      className="bg-white/90 hover:bg-white p-2 rounded-lg shadow-lg transition"
                      title={show3D ? "Show Photo" : "View 3D Model"}
                    >
                      <Gem className="w-5 h-5 text-emerald-600" />
                    </button>
                    <button
                      onClick={() => setModalLot(lot)}
                      className="bg-white/90 hover:bg-white p-2 rounded-lg shadow-lg transition"
                      title="Full Screen 3D View"
                    >
                      <Maximize2 className="w-5 h-5 text-emerald-600" />
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{lot.name}</h3>
                  <p className="text-gray-600 mb-4">{lot.description}</p>
                  
                  <div className="flex items-center justify-between mb-4 pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-500">Current Bid</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        ${lot.currentBid.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 flex items-center justify-end">
                        <Clock className="w-4 h-4 mr-1" />
                        Time Left
                      </p>
                      <p className={`text-lg font-semibold ${isEnded ? 'text-red-600' : 'text-gray-800'}`}>
                        {isEnded ? 'ENDED' : formatTime(timeRemaining)}
                      </p>
                    </div>
                  </div>

                  {!isEnded && (
                    <div className="space-y-3">
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          placeholder={`Min: $${(lot.currentBid + lot.minIncrement).toLocaleString()}`}
                          value={bidAmount}
                          onChange={(e) => setBidAmount(e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, () => handleBid(lot.id))}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        <button
                          onClick={() => handleBid(lot.id)}
                          className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition"
                        >
                          Bid
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Minimum increment: ${lot.minIncrement.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {lot.bids.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={() => setSelectedLot(selectedLot === lot.id ? null : lot.id)}
                        className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        {selectedLot === lot.id ? 'Hide' : 'Show'} Bid History ({lot.bids.length})
                      </button>
                      
                      {selectedLot === lot.id && (
                        <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                          {[...lot.bids].reverse().map((bid, idx) => (
                            <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                              <span className="font-medium">{bid.bidder}</span>
                              <span className="text-emerald-600">${bid.amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default EmeraldAuction;